import type { SpecialLocale } from "./specialLocaleTypes"

export type PlannedLanguageCode =
  | SpecialLocale
  | "az"
  | "id"
  | "ms"
  | "cs"
  | "de"
  | "en"
  | "es"
  | "fr"
  | "it"
  | "sw"
  | "nl"
  | "pl"
  | "pt-BR"
  | "ro"
  | "sv"
  | "tl"
  | "tr"
  | "vi"
  | "be"
  | "kk"
  | "ky"
  | "ru"
  | "tg"
  | "uk"
  | "uz"
  | "hy"
  | "ka"
  | "ar"
  | "fa"
  | "ur"
  | "bn"
  | "hi"
  | "si"
  | "ta"
  | "th"
  | "ko"
  | "ja"
  | "zh"

export type LanguageDirection = "ltr" | "rtl"
export type PlannedLanguageAvailability = "active" | "comingSoon"

interface PlannedLanguageBase {
  code: PlannedLanguageCode
  flagCode: string
  selfLabel: string
  direction: LanguageDirection
  availability: PlannedLanguageAvailability
  comingSoonLabel?: string
}

type CountrySortedLanguage = PlannedLanguageBase & {
  pinnedOrder?: never
  sortRegionCode: string
  sortCountryFallback: string
}

export type PlannedLanguage = CountrySortedLanguage | (PlannedLanguageBase & {
  pinnedOrder: number
  sortRegionCode?: string
  sortCountryFallback?: string
})

// Final public language matrix: 38 ordinary interface languages.
// Public picker order is fixed and identical for every country and UI locale.
// English is permanently pinned at position 1; all other languages preserve the approved relative order.
// Every row displays the approved self-name. All 38 public languages are active.
// Uzbek is intentionally exposed only as Cyrillic Ўзбекча; there is no public Latin Uzbek variant.
// Special/fantasy locales remain supported internally but are intentionally hidden.
export const PLANNED_LANGUAGES: readonly PlannedLanguage[] = [
  { code: "en", flagCode: "en", selfLabel: "English", direction: "ltr", availability: "active", sortRegionCode: "GB", sortCountryFallback: "United Kingdom" },
  { code: "az", flagCode: "az", selfLabel: "Azərbaycan", direction: "ltr", availability: "active", sortRegionCode: "AZ", sortCountryFallback: "Azerbaijan" },
  { code: "id", flagCode: "id", selfLabel: "Bahasa Indonesia", direction: "ltr", availability: "active", sortRegionCode: "ID", sortCountryFallback: "Indonesia" },
  { code: "ms", flagCode: "ms", selfLabel: "Bahasa Melayu", direction: "ltr", availability: "active", sortRegionCode: "MY", sortCountryFallback: "Malaysia" },
  { code: "cs", flagCode: "cs", selfLabel: "Čeština", direction: "ltr", availability: "active", sortRegionCode: "CZ", sortCountryFallback: "Czechia" },
  { code: "de", flagCode: "de", selfLabel: "Deutsch", direction: "ltr", availability: "active", sortRegionCode: "DE", sortCountryFallback: "Germany" },
  { code: "es", flagCode: "es", selfLabel: "Español", direction: "ltr", availability: "active", sortRegionCode: "ES", sortCountryFallback: "Spain" },
  { code: "fr", flagCode: "fr", selfLabel: "Français", direction: "ltr", availability: "active", sortRegionCode: "FR", sortCountryFallback: "France" },
  { code: "it", flagCode: "it", selfLabel: "Italiano", direction: "ltr", availability: "active", sortRegionCode: "IT", sortCountryFallback: "Italy" },
  { code: "sw", flagCode: "sw", selfLabel: "Kiswahili", direction: "ltr", availability: "active", sortRegionCode: "KE", sortCountryFallback: "Kenya" },
  { code: "nl", flagCode: "nl", selfLabel: "Nederlands", direction: "ltr", availability: "active", sortRegionCode: "NL", sortCountryFallback: "Netherlands" },
  { code: "pl", flagCode: "pl", selfLabel: "Polski", direction: "ltr", availability: "active", sortRegionCode: "PL", sortCountryFallback: "Poland" },
  { code: "pt-BR", flagCode: "pt-br", selfLabel: "Português", direction: "ltr", availability: "active", sortRegionCode: "BR", sortCountryFallback: "Brazil" },
  { code: "ro", flagCode: "ro", selfLabel: "Română", direction: "ltr", availability: "active", sortRegionCode: "RO", sortCountryFallback: "Romania" },
  { code: "sv", flagCode: "sv", selfLabel: "Svenska", direction: "ltr", availability: "active", sortRegionCode: "SE", sortCountryFallback: "Sweden" },
  { code: "tl", flagCode: "tl", selfLabel: "Tagalog / Filipino", direction: "ltr", availability: "active", sortRegionCode: "PH", sortCountryFallback: "Philippines" },
  { code: "tr", flagCode: "tr", selfLabel: "Türkçe", direction: "ltr", availability: "active", sortRegionCode: "TR", sortCountryFallback: "Turkey" },
  { code: "vi", flagCode: "vi", selfLabel: "Tiếng Việt", direction: "ltr", availability: "active", sortRegionCode: "VN", sortCountryFallback: "Vietnam" },
  { code: "be", flagCode: "be", selfLabel: "Беларуская", direction: "ltr", availability: "active", sortRegionCode: "BY", sortCountryFallback: "Belarus" },
  { code: "kk", flagCode: "kk", selfLabel: "Қазақша", direction: "ltr", availability: "active", sortRegionCode: "KZ", sortCountryFallback: "Kazakhstan" },
  { code: "ky", flagCode: "ky", selfLabel: "Кыргызча", direction: "ltr", availability: "active", sortRegionCode: "KG", sortCountryFallback: "Kyrgyzstan" },
  { code: "ru", flagCode: "ru", selfLabel: "Русский", direction: "ltr", availability: "active", sortRegionCode: "RU", sortCountryFallback: "Russia" },
  { code: "tg", flagCode: "tg", selfLabel: "Тоҷикӣ", direction: "ltr", availability: "active", sortRegionCode: "TJ", sortCountryFallback: "Tajikistan" },
  { code: "uk", flagCode: "uk", selfLabel: "Українська", direction: "ltr", availability: "active", sortRegionCode: "UA", sortCountryFallback: "Ukraine" },
  { code: "uz", flagCode: "uz", selfLabel: "Ўзбекча", direction: "ltr", availability: "active", sortRegionCode: "UZ", sortCountryFallback: "Uzbekistan" },
  { code: "hy", flagCode: "hy", selfLabel: "Հայերեն", direction: "ltr", availability: "active", sortRegionCode: "AM", sortCountryFallback: "Armenia" },
  { code: "ka", flagCode: "ka", selfLabel: "ქართული", direction: "ltr", availability: "active", sortRegionCode: "GE", sortCountryFallback: "Georgia" },
  { code: "ar", flagCode: "ar", selfLabel: "العربية", direction: "rtl", availability: "active", sortRegionCode: "SA", sortCountryFallback: "Saudi Arabia" },
  { code: "fa", flagCode: "fa", selfLabel: "فارسی", direction: "rtl", availability: "active", sortRegionCode: "IR", sortCountryFallback: "Iran" },
  { code: "ur", flagCode: "ur", selfLabel: "اُردُو", direction: "rtl", availability: "active", sortRegionCode: "PK", sortCountryFallback: "Pakistan" },
  { code: "bn", flagCode: "bn", selfLabel: "বাংলা", direction: "ltr", availability: "active", sortRegionCode: "BD", sortCountryFallback: "Bangladesh" },
  { code: "hi", flagCode: "hi", selfLabel: "हिन्दी", direction: "ltr", availability: "active", sortRegionCode: "IN", sortCountryFallback: "India" },
  { code: "si", flagCode: "si", selfLabel: "සිංහල", direction: "ltr", availability: "active", sortRegionCode: "LK", sortCountryFallback: "Sri Lanka" },
  { code: "ta", flagCode: "ta", selfLabel: "தமிழ்", direction: "ltr", availability: "active", sortRegionCode: "LK", sortCountryFallback: "Sri Lanka" },
  { code: "th", flagCode: "th", selfLabel: "ไทย", direction: "ltr", availability: "active", sortRegionCode: "TH", sortCountryFallback: "Thailand" },
  { code: "ko", flagCode: "ko", selfLabel: "한국어", direction: "ltr", availability: "active", sortRegionCode: "KR", sortCountryFallback: "South Korea" },
  { code: "ja", flagCode: "ja", selfLabel: "日本語", direction: "ltr", availability: "active", sortRegionCode: "JP", sortCountryFallback: "Japan" },
  { code: "zh", flagCode: "zh", selfLabel: "简体中文", direction: "ltr", availability: "active", sortRegionCode: "SG", sortCountryFallback: "Singapore" },
]

export function getPlannedLanguagesForPicker(
  _resolveLabel: (language: PlannedLanguage) => string,
  _locale?: string,
): PlannedLanguage[] {
  return [...PLANNED_LANGUAGES]
}
